import { useOrganizationStore } from '@/store/organization';
import { useUserStore } from '@/store/user';
import { Organization_Feature } from 'hiber-grpc/src/customer_compiled/organization';
import TagService from 'hiber-grpc/src/services/tags';
import { getGrpcClient } from '@/services/grpc-client';
import { Rule } from '@/acl/rules';
import WebhookService from 'hiber-grpc/src/services/webhooks';
import TokensService from 'hiber-grpc/src/services/tokens';
import SlackIntegrationService from 'hiber-grpc/src/services/slack-integration';
import MqttService from 'hiber-grpc/src/services/mqtt';
import CertificatesService from 'hiber-grpc/src/services/certificates';
import ModemMessageBodyParserService from 'hiber-grpc/src/services/modem-message-body-parser';
import FieldService from 'hiber-grpc/src/services/field';
import ProcessPointService from 'hiber-grpc/src/services/process-points';
import ModemService from 'hiber-grpc/src/services/modem';
import { ClientError, Status } from 'nice-grpc-web';
import { getAuthProviderName } from '@/auth/AuthFactory';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

async function webhookBeforeEnterGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  if (!to.params.id || Number.isNaN(parseInt(to.params.id))) {
    next({ name: 'developer-tools', query: { organization: to.query.organization } });
  } else {
    const webhooks = await WebhookService.getInstance(getGrpcClient).list({
      selection: { webhooks: { include: [to.params.id] } },
    });
    if (webhooks.webhooks.length) {
      next();
    } else {
      next({ name: 'view-developer-tools', query: { organization: to.query.organization } });
    }
  }
}

export const routes = {
  home: {
    name: 'home',
    path: '/',
    component: () => import('../templates/AppLayout.vue'),
    meta: { requiresAuth: true },
    redirect: '/dashboard',
    beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
      const organizationStore = useOrganizationStore();
      try {
        // fetch organization details and check for permissions and feature flags
        await organizationStore.fetchOrganization(organizationStore.organizationImpersonation);

        const { meta } = to;
        const isAllowed = organizationStore.hasFeatures(meta.features as Organization_Feature[]);
        if (!isAllowed) {
          next({ path: routes.home.path, query: { ...to.query } });
        } else {
          next();
        }
      } catch (error) {
        const userStore = useUserStore();

        // Retry without impersonation only when that organization is forbidden.
        if (
          organizationStore.organizationImpersonation &&
          error instanceof ClientError &&
          error.code === Status.PERMISSION_DENIED
        ) {
          organizationStore.organizationImpersonation = '';
          organizationStore.organization = null;
          return next({ path: routes.home.path, replace: true });
        }

        const isOry = !userStore.hasLocalAuthToken && !userStore.useLocalTokenAuth && getAuthProviderName() === 'ory';

        if (!isOry && error instanceof ClientError && error.code === Status.UNAUTHENTICATED) {
          return next({
            path: userStore.useLocalTokenAuth ? routes.tokenLogin.path : routes.login.path,
            replace: true,
          });
        }

        // An outage, permission failure outside impersonation, or rejected
        // backend JWT does not establish that the Ory cookie is invalid.
        return next({
          path: routes.serviceUnavailable.path,
          query: { retry: to.fullPath },
          replace: true,
        });
      }
    },
  },
  devices: {
    name: 'devices',
    path: '/devices',
    alias: '/devices',
    component: () => import('../pages/devices/Devices.vue'),
    meta: {
      can: Rule.VIEW_DEVICES,
    },
    children: [
      {
        path: ':id/assign',
        name: 'device-assignment',
        component: () => import('../pages/devices/DeviceAssignment.vue'),
        props: true,
        meta: {
          label: 'Process Point Assignment',
        },
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.id) {
            next({ name: 'devices', query: { organization: to.query.organization } });
          } else {
            try {
              const processPointsQuery = await ModemService.getInstance(getGrpcClient).list({
                selection: { modems: { include: [to.params.id] } },
              });
              if (!processPointsQuery.modems.length) {
                return next({ name: 'devices', query: { organization: to.query.organization } });
              }
              next();
            } catch (e) {
              next({ name: 'devices', query: { organization: to.query.organization } });
            }
          }
        },
      },
    ],
  },
  maintenance: {
    name: 'maintenance',
    path: '/maintenance',
    component: () => import('../pages/maintenance/MaintenanceLayout.vue'),
    meta: {
      can: Rule.MAINTENANCE,
      onDeniedRoute: '/dashboard',
    },
  },
  processPoints: {
    name: 'processPoints',
    path: '/process-points',
    redirect: 'process-points/list',
    component: () => import('../pages/process-points/ProcessPointsLayout.vue'),
    meta: {
      can: Rule.VIEW_PROCESS_POINTS,
      onDeniedRoute: '/dashboard',
    },
    children: [
      {
        path: 'list',
        name: 'process-points-table',
        component: () => import('../pages/process-points/ProcessPoints.vue'),
        meta: {},
      },
      {
        path: ':id/assign',
        name: 'process-point-assignment',
        component: () => import('../pages/process-points/ProcessPointAssignments.vue'),
        props: true,
        meta: {
          label: 'Device Assignment',
        },
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.id) {
            next({ name: 'process-points', query: { organization: to.query.organization } });
          } else {
            try {
              const processPointsQuery = await ProcessPointService.getInstance(getGrpcClient).list({
                selection: { identifiers: [to.params.id] },
              });
              if (!processPointsQuery.processPoints.length) {
                return next({ name: 'process-points', query: { organization: to.query.organization } });
              }
              next();
            } catch (e) {
              next({ name: 'process-points', query: { organization: to.query.organization } });
            }
          }
        },
      },
      {
        path: 'create',
        name: 'process-point-create',
        component: () => import('../pages/process-points/ProcessPointCreate.vue'),
        props: true,
        meta: {
          label: 'Create Process Point',
        },
      },
    ],
  },
  dashboard: {
    name: 'dashboard',
    path: '/dashboard',
    component: () => import('../pages/dashboard/DashboardLayout.vue'),
    props: true,
    meta: {
      can: Rule.VIEW_DEVICES,
      label: 'HiberHilo',
    },
    beforeEnter: (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
      if (to.query.view) {
        return next();
      }

      const userStore = useUserStore();
      const defaultView = userStore.dashBoardView;

      if (defaultView) {
        return next({
          path: to.path,
          query: { ...to.query, view: defaultView },
          replace: true,
        });
      }

      return next();
    },
    children: [
      {
        path: '',
        name: 'overview-page',
        component: () => import('../pages/dashboard/DashboardIndex.vue'),
      },
      {
        path: 'well/:id?',
        name: 'well-page',
        redirect: (to) => ({
          name: 'sensor-page',
          params: { tab: 'all', id: to.params.id },
          query: { ...to.query },
        }),
        component: () => import('../pages/dashboard/WellDashboardLayout.vue'),
        props: true,
        children: [
          {
            path: 'tab/:tab',
            name: 'sensor-page',
            component: () => import('../pages/dashboard/WellDashboardIndex.vue'),
            props: true,
            beforeEnter: async (
              to: RouteLocationNormalized,
              _from: RouteLocationNormalized,
              next: NavigationGuardNext,
            ) => {
              if (!to.params.id || Number.isNaN(parseInt(to.params.id))) {
                next({ name: 'overview-page', query: { organization: to.query.organization } });
              } else {
                const tagsQuery = await TagService.getInstance(getGrpcClient).list({
                  selection: { filter: { include: [to.params.id] } },
                });
                if (tagsQuery.tags.length < 1) {
                  next({ name: 'overview-page', query: { organization: to.query.organization } });
                } else {
                  next();
                }
              }
            },
          },
        ],
      },
    ],
  },
  settings: {
    name: 'settings',
    path: '/settings',
    component: () => import('../pages/settings/SettingsPage.vue'),
    children: [
      {
        path: '',
        redirect: '/settings/tags',
        name: 'view-settings',
        component: () => import('../pages/settings/ViewSettings.vue'),
        children: [
          {
            fullPath: '/settings/tags',
            path: 'tags',
            name: 'tags-settings',
            component: () => import('../pages/settings/TagsSettings.vue'),
            meta: {
              label: 'Tags',
            },
          },
          {
            fullPath: '/settings/exports',
            path: 'exports',
            name: 'exports',
            component: () => import('../pages/settings/Exports.vue'),
            meta: {
              label: 'Exports',
              can: Rule.EXPORT,
              onDeniedRoute: '/settings/tags',
            },
          },
          {
            fullPath: '/settings/alarms',
            path: 'alarms',
            name: 'alarms-settings',
            component: () => import('../pages/settings/AlarmSettings.vue'),
            meta: {
              label: 'Alarms',
              can: Rule.VIEW_ALARMS,
              onDeniedRoute: '/settings/tags',
            },
          },
          {
            fullPath: '/settings/accounts',
            path: 'accounts',
            name: 'accounts-settings',
            component: () => import('../pages/settings/AccountsManagement.vue'),
            meta: {
              label: 'Users & Accounts',
              can: Rule.VIEW_USERS,
              onDeniedRoute: '/settings/tags',
            },
          },
          {
            fullPath: '/settings/organizations',
            path: 'organizations',
            name: 'organization-settings',
            component: () => import('../pages/settings/Organizations.vue'),
            meta: {
              label: 'Sub-organizations',
            },
          },
          {
            fullPath: '/settings/organization-info',
            path: 'organization-info',
            name: 'organization-info',
            component: () => import('../pages/settings/OrganizationInfo.vue'),
            meta: {
              label: 'Organization info',
            },
          },
        ],
      },
      {
        path: 'tags/:id/update',
        name: 'tag-update',
        component: () => import('../pages/settings/TagManagement.vue'),
        props: true,
        meta: {
          label: 'Edit Tag',
        },
      },
      {
        path: 'tags/create',
        name: 'tag-create',
        component: () => import('../pages/settings/TagManagement.vue'),
        props: true,
        meta: {
          label: 'Create Tag',
        },
      },
      {
        path: 'alarms/:id/update',
        name: 'alarm-update',
        component: () => import('../pages/settings/AlarmManagement.vue'),
        props: true,
        meta: {
          label: 'Edit Alarm',
        },
      },
      {
        path: 'alarms/create',
        name: 'alarm-create',
        component: () => import('../pages/settings/AlarmManagement.vue'),
        props: true,
        meta: {
          label: 'Create Alarm',
        },
      },
    ],
  },
  transfers: {
    path: '/transfers',
    redirect: '/transfers/inbound',
    name: 'transfers',
    meta: {
      can: Rule.VIEW_TRANSFERS,
    },
    component: () => import('../pages/transfers/TransfersPage.vue'),
    children: [
      {
        fullPath: '/transfers/inbound',
        path: '/transfers/inbound',
        name: 'inbound-transfers',
        component: () => import('../pages/transfers/InboundTransfersPage.vue'),
        meta: {
          label: 'Inbound transfers',
        },
      },
      {
        fullPath: '/transfers/outbound',
        path: '/transfers/outbound',
        name: 'outbound-transfers',
        component: () => import('../pages/transfers/OutboundTransfersPage.vue'),
        meta: {
          label: 'Outbound transfers',
        },
      },
    ],
  },
  developer: {
    path: '/developer-tools',
    name: 'developer-tools',
    component: () => import('../pages/developer-tools/DeveloperTools.vue'),
    children: [
      {
        path: '',
        redirect: '/developer-tools/webhooks',
        name: 'view-developer-tools',
        component: () => import('../pages/developer-tools/TabsPage.vue'),
        children: [
          {
            fullPath: '/developer-tools/webhooks',
            path: 'webhooks',
            component: () => import('../pages/developer-tools/Webhooks.vue'),
            meta: {
              label: 'Webhooks',
              can: Rule.PUBLISHERS,
            },
          },
          {
            fullPath: '/developer-tools/tokens',
            path: 'tokens',
            component: () => import('../pages/developer-tools/Tokens.vue'),
            meta: {
              label: 'Tokens',
              can: Rule.MANAGE_TOKENS,
            },
          },
          {
            fullPath: '/developer-tools/parsers',
            path: 'parsers',
            component: () => import('../pages/developer-tools/Parsers.vue'),
            meta: {
              label: 'Parsers',
              can: Rule.MANAGE_PARSERS,
            },
          },
          {
            fullPath: '/developer-tools/fields',
            path: 'fields',
            component: () => import('../pages/developer-tools/Fields.vue'),
            meta: {
              label: 'Fields',
              can: Rule.MANAGE_PARSERS,
            },
          },
          {
            fullPath: '/developer-tools/slack',
            path: 'slack',
            component: () => import('../pages/developer-tools/Slack.vue'),
            meta: {
              label: 'Slack',
              can: Rule.PUBLISHERS,
            },
          },
          {
            fullPath: '/developer-tools/mqtt',
            path: 'mqtt',
            component: () => import('../pages/developer-tools/Mqtt.vue'),
            meta: {
              label: 'MQTT',
              can: Rule.PUBLISHERS,
            },
          },
          {
            fullPath: '/developer-tools/certificates',
            path: 'certificates',
            component: () => import('../pages/developer-tools/Certificates.vue'),
            meta: {
              label: 'Certificates',
              can: Rule.CERTIFICATES,
            },
          },
        ],
      },
      {
        path: 'webhooks/:id/history',
        name: 'webhook-history',
        component: () => import('../pages/developer-tools/WebhookHistory.vue'),
        props: true,
        beforeEnter: webhookBeforeEnterGuard,
        meta: {
          label: 'Webhook history',
          can: Rule.PUBLISHERS,
        },
      },
      {
        path: 'webhooks/create',
        name: 'webhook-create',
        component: () => import('../pages/developer-tools/WebhookManagement.vue'),
        meta: {
          label: 'Create webhook',
          can: Rule.PUBLISHERS,
        },
      },
      {
        path: 'tokens/create',
        name: 'token-create',
        component: () => import('../pages/developer-tools/TokenManagement.vue'),
        meta: {
          label: 'Create token',
          can: Rule.MANAGE_TOKENS,
        },
      },
      {
        path: 'parsers/create',
        name: 'parser-create',
        component: () => import('../pages/developer-tools/ParserManagement.vue'),
        meta: {
          label: 'Create parser',
          can: Rule.MANAGE_PARSERS,
        },
      },
      {
        path: 'mqtt/create',
        name: 'mqtt-create',
        component: () => import('../pages/developer-tools/MqttManagement.vue'),
        meta: {
          label: 'Create MQTT Publisher',
          can: Rule.PUBLISHERS,
        },
      },
      {
        path: 'certificates/upload',
        name: 'certificates-upload',
        component: () => import('../pages/developer-tools/CertificateManagement.vue'),
        meta: {
          label: 'Upload Certificate',
          can: Rule.MANAGE_CERTIFICATES,
        },
      },
      {
        path: 'slack/create',
        name: 'slack-create',
        component: () => import('../pages/developer-tools/SlackPublisherManagement.vue'),
        meta: {
          label: 'Create slack integration',
          can: Rule.PUBLISHERS,
        },
      },
      {
        path: 'webhooks/:id/update',
        name: 'webhook-edit',
        component: () => import('../pages/developer-tools/WebhookManagement.vue'),
        props: true,
        beforeEnter: webhookBeforeEnterGuard,
        meta: {
          label: 'Edit webhook',
          can: Rule.PUBLISHERS,
        },
      },
      {
        path: 'parsers/:identifier/update',
        name: 'parser-edit',
        component: () => import('../pages/developer-tools/ParserManagement.vue'),
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.identifier) {
            next({ path: '/developer-tools/parsers', query: { organization: to.query.organization } });
          } else {
            const parsers = await ModemMessageBodyParserService.getInstance(getGrpcClient).list({
              selection: { search: to.params.identifier },
            });
            if (parsers.parsers.length) {
              next();
            } else {
              next({ path: '/developer-tools/parsers', query: { organization: to.query.organization } });
            }
          }
        },
        meta: {
          label: 'Edit parser',
        },
      },
      {
        path: 'fields/create',
        name: 'field-create-without-parser',
        component: () => import('../pages/developer-tools/FieldManagement.vue'),
        meta: {
          label: 'Create field',
        },
      },
      {
        path: 'fields/:parserIdentifier/create',
        name: 'field-create',
        component: () => import('../pages/developer-tools/FieldManagement.vue'),
        meta: {
          label: 'Create field',
        },
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.parserIdentifier) {
            next({ path: '/developer-tools/parsers', query: { organization: to.query.organization } });
          } else {
            const parsers = await ModemMessageBodyParserService.getInstance(getGrpcClient).list({
              selection: { search: to.params.parserIdentifier },
            });
            if (parsers.parsers.length) {
              next();
            } else {
              next({ path: '/developer-tools/parsers', query: { organization: to.query.organization } });
            }
          }
        },
      },
      {
        path: 'fields/:identifier/update',
        name: 'field-edit',
        component: () => import('../pages/developer-tools/FieldManagement.vue'),
        meta: {
          label: 'Edit field',
        },
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.identifier) {
            next({ path: '/developer-tools/fields', query: { organization: to.query.organization } });
          } else {
            const fields = await FieldService.getInstance(getGrpcClient).listFields({
              selection: { fieldIdentifiers: [to.params.identifier], includeOperational: true },
            });
            if (fields.fields.length) {
              next();
            } else {
              next({ path: '/developer-tools/fields', query: { organization: to.query.organization } });
            }
          }
        },
      },
      {
        path: 'tokens/:name/update',
        name: 'token-edit',
        component: () => import('../pages/developer-tools/TokenManagement.vue'),
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.name) {
            next({ path: '/developer-tools/tokens', query: { organization: to.query.organization } });
          } else {
            const tokens = await TokensService.getInstance(getGrpcClient).list({
              selection: { name: to.params.name },
            });
            if (tokens.tokens.length) {
              next();
            } else {
              next({ path: '/developer-tools/tokens', query: { organization: to.query.organization } });
            }
          }
        },
        meta: {
          label: 'Edit token',
        },
      },
      {
        path: 'mqtt/:id/update',
        name: 'mqtt-edit',
        component: () => import('../pages/developer-tools/MqttManagement.vue'),
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.id) {
            next({ path: '/developer-tools/mqtt', query: { organization: to.query.organization } });
          } else {
            const publishersResponse = await MqttService.getInstance(getGrpcClient).list({
              selection: { publishers: { include: [to.params.id] } },
            });
            if (publishersResponse.publishers.length) {
              next();
            } else {
              next({ path: '/developer-tools/mqtt', query: { organization: to.query.organization } });
            }
          }
        },
        meta: {
          label: 'Edit MQTT Publisher',
        },
      },
      {
        path: 'certificates/:id/update',
        name: 'certificate-edit',
        component: () => import('../pages/developer-tools/CertificateManagement.vue'),
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.id) {
            next({ path: '/developer-tools/certificates', query: { organization: to.query.organization } });
          } else {
            const certificatesResponse = await CertificatesService.getInstance(getGrpcClient).list({
              selection: { certificateIds: [to.params.id] },
            });
            if (certificatesResponse.certificates.length) {
              next();
            } else {
              next({ path: '/developer-tools/certificates', query: { organization: to.query.organization } });
            }
          }
        },
        meta: {
          label: 'Edit Certificate',
        },
      },
      {
        path: 'slack/:id/update',
        name: 'slack-edit',
        component: () => import('../pages/developer-tools/SlackPublisherManagement.vue'),
        props: true,
        beforeEnter: async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.id || Number.isNaN(parseInt(to.params.id))) {
            next({ name: 'developer-tools', query: { organization: to.query.organization } });
          } else {
            const slackPublishers = await SlackIntegrationService.getInstance(getGrpcClient).list({
              selection: { publishers: { include: [to.params.id] } },
            });
            if (slackPublishers.publishers.length) {
              next();
            } else {
              next({ name: 'view-developer-tools', query: { organization: to.query.organization } });
            }
          }
        },
        meta: {
          label: 'Edit Slack integration',
        },
      },
    ],
  },
  changelog: {
    path: '/changelog',
    redirect: '/changelog/mission-control',
    component: () => import('../pages/changelog/ChangelogPage.vue'),
    meta: {
      label: 'Changelog',
    },
    children: [
      {
        fullPath: '/changelog/mission-control',
        path: 'mission-control',
        component: () => import('../pages/changelog/MissionControlPage.vue'),
        meta: {
          label: 'Mission Control',
        },
      },
      {
        fullPath: '/changelog/customer-api',
        path: 'customer-api',
        component: () => import('../pages/changelog/CustomerApiPage.vue'),
        meta: {
          label: 'API',
        },
      },
      {
        fullPath: '/changelog/support-api',
        path: 'support-api',
        component: () => import('../pages/changelog/SupportApiPage.vue'),
        meta: {
          label: 'Operations',
          can: Rule.CUSTOMER_SUPPORT,
          onDeniedRoute: '/changelog/mission-control',
        },
      },
      {
        fullPath: '/changelog/cli',
        path: 'cli',
        component: () => import('../pages/changelog/CliPage.vue'),
        meta: {
          label: 'CLI',
        },
      },
    ],
  },
  support: {
    fullPath: '/login/support',
    path: 'support',
    name: 'support',
    component: () => import('../pages/auth/ZendeskPage.vue'),
    meta: { requiresAuth: true },
  },
  user: {
    path: '/user',
    redirect: '/user/profile',
    component: () => import('../pages/user/UserPage.vue'),
    children: [
      {
        fullPath: '/user/profile',
        path: 'profile',
        name: 'profile-settings',
        component: () => import('../pages/user/Profile.vue'),
        meta: {
          label: 'Personal profile',
        },
      },
      {
        fullPath: '/user/general',
        path: 'general',
        alias: '/settings/general',
        name: 'general-settings',
        component: () => import('../pages/user/GeneralSettings.vue'),
        meta: {
          label: 'Units & date-time format',
        },
      },
      {
        fullPath: '/user/email-preferences',
        path: 'email-preferences',
        alias: '/settings/email-preferences',
        name: 'email-preferences',
        component: () => import('../pages/user/EmailPreferences.vue'),
        meta: {
          label: 'Email preferences',
        },
      },
    ],
  },
  login: {
    path: '/login',
    component: () => import('../templates/HomeLayout.vue'),
  },
  mfa: {
    fullPath: '/login/mfa',
    path: 'mfa',
    meta: { transition: 'slide-fade-left', requiresAuth: true },
    children: [
      {
        fullPath: '/login/mfa/enroll',
        path: 'enroll',
        component: () => import('../pages/auth/mfa/enroll/MfaEnrollView.vue'),
      },
      {
        path: '',
        component: () => import('../pages/auth/mfa/submit/MfaSubmitView.vue'),
      },
    ],
  },
  signIn: {
    path: '',
    component: () => import('../pages/auth/login/LoginView.vue'),
    meta: { transition: 'slide-fade-left', can: Rule.PASSWORD_AUTH },
  },
  tokenLogin: {
    path: '/login/token',
    component: () => import('../pages/auth/login/TokenLogin.vue'),
    meta: { transition: 'slide-fade-right', can: Rule.TOKEN_AUTH },
  },
  createUser: {
    fullPath: '/login/create',
    path: 'create',
    component: () => import('../pages/auth/signup/SignupView.vue'),
    meta: { transition: 'slide-fade-right' },
  },
  resetPassword: {
    fullPath: '/login/reset-password',
    path: 'reset-password',
    component: () => import('../pages/auth/reset/ResetPasswordView.vue'),
    meta: { transition: 'slide-fade-right', requiresAuth: true },
  },
  forgotPassword: {
    fullPath: '/login/forgot-password',
    path: 'forgot-password',
    component: () => import('../pages/auth/forgot/ForgotPasswordView.vue'),
    meta: { transition: 'slide-fade-right' },
  },
  forgotPasswordOtp: {
    fullPath: '/login/forgot-password/otp',
    path: 'forgot-password/otp',
    component: () => import('../pages/auth/forgot/ForgotPasswordView.vue'),
    meta: { transition: 'slide-fade-right' },
  },
  emailVerification: {
    fullPath: '/login/email-verification',
    path: 'email-verification',
    component: () => import('../pages/auth/verify/VerifyEmailView.vue'),
    meta: { transition: 'slide-fade-left', requiresAuth: true },
    beforeEnter: async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      to.meta.autoStartVerification = from.path !== routes.createUser.fullPath;
    },
  },
  termsAndConditions: {
    fullPath: '/login/terms-and-conditions',
    path: 'terms-and-conditions',
    component: () => import('../pages/auth/TermsAndConditionsPage.vue'),
    meta: { transition: 'slide-fade-left', requiresAuth: true },
  },
  organizations: {
    path: '/organization',
    component: () => import('../templates/HomeLayout.vue'),
    redirect: '/organization/requests',
  },
  organizationsRequest: {
    fullPath: '/organization/requests',
    path: 'requests',
    component: () => import('../pages/organization/OrganizationRequestsPage.vue'),
    meta: { transition: 'slide-fade-right', requiresAuth: true },
  },
  organizationsJoin: {
    fullPath: '/organization/join',
    path: 'join',
    component: () => import('../pages/organization/JoinOrganizationPage.vue'),
    meta: { transition: 'slide-fade-left', requiresAuth: true },
  },
  organizationsInvites: {
    fullPath: '/organization/invites',
    path: 'invites',
    component: () => import('../pages/organization/OrganizationInvitesPage.vue'),
    meta: { transition: 'slide-fade-right', requiresAuth: true },
  },
  error: {
    path: '/error',
    name: 'error',
    component: () => import('../pages/auth/ErrorPage.vue'),
    meta: {
      label: 'Error',
    },
  },
  serviceUnavailable: {
    fullPath: '/login/service-unavailable',
    path: '/login/service-unavailable',
    name: 'service-unavailable',
    component: () => import('../pages/auth/ServiceUnavailablePage.vue'),
    meta: {
      label: 'Service Unavailable',
    },
  },
};

export default [
  {
    ...routes.error,
  },
  {
    ...routes.login,
    children: [
      routes.signIn,
      routes.tokenLogin,
      routes.createUser,
      routes.resetPassword,
      routes.forgotPassword,
      routes.forgotPasswordOtp,
      routes.emailVerification,
      routes.mfa,
      routes.termsAndConditions,
      routes.support,
      routes.serviceUnavailable,
    ],
  },
  {
    ...routes.organizations,
    children: [routes.organizationsRequest, routes.organizationsJoin, routes.organizationsInvites],
  },
  {
    ...routes.home,
    children: [
      routes.dashboard,
      routes.devices,
      routes.maintenance,
      routes.processPoints,
      routes.changelog,
      routes.user,
      routes.settings,
      routes.transfers,
      routes.developer,
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => {
      return { path: '/' };
    },
  },
];
